import proxy from "api/proxy";
import { useGlobal } from "storev2/globalStore";

import {
  isValidEmail,
  isValidPhone,
  isValidName,
  isValidAddress,
  splitFullName,
  locationToAddress,
} from "tools/helpers";
const getOptions = () => {
  const options = [
    { id: "11", name: "registro_civil" },
    { id: "12", name: "tarjeta_identidad" },
    { id: "13", name: "cedula_ciudadania" },
    { id: "21", name: "tarjeta_extranjeria" },
    { id: "22", name: "cedula_extranjeria" },
    { id: "31", name: "nit" },
    { id: "41", name: "pasaporte" },
    { id: "42", name: "tipo_documento_extranjero" },
    { id: "47", name: "pep" },
    { id: "50", name: "nit_otro_pais" },
    { id: "91", name: "nuip" },
  ];
  return options;
};
const typeUserHealthOptions = [
  { id: "01", name: "Contributivo cotizante" },
  { id: "02", name: "Contributivo beneficiario" },
  { id: "03", name: "Contributivo adicional" },
  { id: "04", name: "Subsidiado" },
  { id: "05", name: "No afiliado" },
  { id: "06", name: "Especial o Excepción cotizante" },
  { id: "07", name: "Especial o Excepción beneficiario" },
  {
    id: "08",
    name: "Personas privadas de la libertad a cargo del Fondo Nacional de Salud",
  },
  { id: "09", name: "Tomador / Amparado ARL" },
  { id: "10", name: "Tomador / Amparado SOAT" },
  {
    id: "11",
    name: "Tomador / Amparado planes voluntarios de salud",
  },
  { id: "12", name: "Particular" },
  {
    id: "13",
    name: "Especial o Excepción no cotizante Ley 352 de 1997",
  },
];

const getSexOptions = [
  { id: "male", name: "male" },
  { id: "female", name: "female" },
];

async function onChangeIdNumber(_, store) {
  const idNumber = store?.active?.id_number ?? store?.store?.id_number;

  if (!idNumber || typeof idNumber !== "string" || idNumber.length < 6) return;

  const res = await proxy.search(
    "party.party",
    [
      ["id_number", "=", idNumber],
      ["is_patient", "=", false],
    ],
    [
      "id",
      "rec_name",
      "name",
      "type_document",
      "main_mobile",
      "main_email",
      "birthday",
      "sex",
      "street",
      "country",
      "born_country",
    ],
  );

  if (!res?.data?.length) return;

  const existing = res.data[0];

  // Convertir a paciente
  const typeUserHealth =
    store?.active?.type_user_health ?? store?.store?.type_user_health ?? "12";

  await proxy.save({
    model: "party.party",
    ids: [existing.id],
    values: {
      is_patient: true,
      type_user_health: typeUserHealth,
    },
  });

  // To write instead of create
  const merged = { ...existing, is_patient: true };

  if (store.store) Object.assign(store.store, merged);
  if (store.active) Object.assign(store.active, merged);
}

async function getCountries() {
  const { countries, loadData } = useGlobal.getState();
  if (countries.length === 0) {
    await loadData();
  }
  return useGlobal.getState().countries;
}

async function defaultCountry() {
  const countries = await getCountries();
  return countries.find((c) => c.code === "CO");
}

function isColombia(record) {
  if (!record?.country) return false;
  if (record?.country.code) return record?.country.code === "CO";
  const found = useGlobal.getState().countries.find((x) => x.id === record?.country.id);
  return found?.code === "CO";
}

function getSubdivisions(_, record) {
  if (!record?.country?.id) return null;
  return ["country", "=", record.country.id];
}

function getCities(_, record) {
  if (!record?.subdivision?.id) return null;
  return ["subdivision", "=", record.subdivision.id];
}

function invisibleByCountry(_, record) {
  return !record?.country;
}

function invisibleCityCo(_, record) {
  return !isColombia(record);
}

function invisibleCity(_, record) {
  return isColombia(record);
}


function requiredSubdivisionCo(record) {
  return isColombia(record) && Boolean(record?.street) ;
}
function requiredCityCo(record) {
  return isColombia(record) && Boolean(record?.street) ;
}

function onChangeCountry(_, store) {
  store.active.subdivision = null;
  store.store.subdivision = null;
  store.active.city_co = null;
  store.store.city_co = null;
  store.active.city = null;
  store.store.city = null;
}
function onChangeSubdivision(_, store) {
  const subdivision = store.active?.subdivision ?? store.store?.subdivision;
  if (!subdivision) return;
  store.active.city_co = null;
  store.store.city_co = null;
}

function layout() {
  const ctxView = {
    model: "party.party",
    beforeSave: locationToAddress,
    activeSearch: true,
    limit: 20,
    domain: [["is_patient", "=", true]],
    form_action: ["edit", "add"],
    table_action: ["add"],
    fields: {
      name: {
        type: "char",
        searchable: true,
        required: true,
        uppercase: true,
        validate: ({ value }) => {
          if (!value) return true;
          return isValidName(value)
            ? true
            : "party.party.validation.invalid_name";
        },
        withChange: async (_, record) => {
          const name = record.active.name || record.store.name || "";
          splitFullName(name, record);
        },
      },
      first_name: { type: "char", uppercase: true },
      second_name: { type: "char", uppercase: true },
      first_family_name: { type: "char", uppercase: true },
      second_family_name: { type: "char", uppercase: true },
      id_number: {
        type: "char",
        searchable: true,
        required: true,
        withChange: onChangeIdNumber,
      },
      is_patient: {
        type: "boolean",
        default: true,
        invisible: true,
      },
      type_document: {
        type: "selection",
        options: getOptions(),
        translate: true,
        required: true,
      },
      sex: {
        type: "selection",
        options: getSexOptions,
        required: true,
        translate: true,
      },
      birthday: {
        type: "date_2",
        required: true,
      },
      main_email: {
        type: "char",
        validate: ({ value }) => {
        if (!value) return true;
        return isValidEmail(value)
          ? true
          : "party.party.validation.invalid_email";
        }
      },
      main_mobile: {
        type: "char",
        required: true,
        validate: ({ value }) => {
          if (!value) return true;
          return isValidPhone(value)
            ? true
            : "party.party.validation.invalid_phone";
        },
      },
      country: {
        type: "many2one",
        model: "country.country",
        required: true,
        preOptions: getCountries,
        default: defaultCountry,
        withChange: onChangeCountry,
      },
      subdivision: {
        type: "many2one",
        model: "country.subdivision",
        recSearch: getSubdivisions,
        withChange: onChangeSubdivision,
        required: requiredSubdivisionCo,
        invisible: invisibleByCountry,
      },
      city_co: {
        type: "many2one",
        model: "country.city",
        recSearch: getCities,
        required: requiredCityCo,
        invisible: invisibleCityCo,
      },
      city: {
        type: "char",
        uppercase: true,
        invisible: invisibleCity,
      },
      born_country: {
        type: "many2one",
        model: "country.country",
        required: true,
      },
      street: {
        type: "char",
        validate: ({ value }) => {
          if (!value) return true;
          return isValidAddress(value);
        },
      },
      type_user_health: {
        type: "selection",
        options: typeUserHealthOptions,
        required: true,
      },
    },
    list: [
      { name: "name", width: "30%" },
      { name: "id_number", width: "10%" },
      { name: "main_mobile", width: "15%" },
      { name: "main_email", width: "15%" },
    ],
    form: [
      { name: "name" },
      { name: "type_document" },
      { name: "id_number" },
      { name: "type_user_health" },
      // { name: "is_patient" },
      { name: "main_email" },
      { name: "main_mobile" },
      { name: "street" },
      { name: "country" },
      { name: "subdivision" },
      { name: "city_co" },
      { name: "city" },
      { name: "born_country" },
      { name: "birthday" },
      { name: "sex" },
    ],
  };
  return ctxView;
}

export { layout };
